import { useEffect, useMemo, useRef, useState } from 'react';
import io from 'socket.io-client';
import {
  Box,
  Button,
  Stack,
  TextField,
  Typography,
  Paper,
  Chip,
  CircularProgress,
} from '@mui/material';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CachedIcon from '@mui/icons-material/Cached';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import MonitorHeartOutlinedIcon from '@mui/icons-material/MonitorHeartOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import ParticipantEPRProvider from './ParticipantEPRContext';
import EPRSocketBridge from './EPRSocketBridge';
import ParticipantApp from '../ParticipantApp';
import { SessionContext } from './SessionContext';
import { useNavigate } from 'react-router-dom';

const SOCKET_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';
const HEADER_BG = '#1e40af';
const HEADER_BORDER = '#60A5FA';
const PAGE_BG = '#F8FAFF';
const CARD_BORDER = '#60A5FA';
const CARD_HEADER_BG = '#dbeafe';

export default function ParticipantJoin() {
  const socketRef = useRef(null);
  const navigate = useNavigate();

  const [pinDigits, setPinDigits] = useState(['', '', '', '', '']);
  const [room, setRoom] = useState(null);
  const [status, setStatus] = useState('idle'); // idle | joining | waiting | running | ended
  const [error, setError] = useState(null);
  const [scenarioId, setScenarioId] = useState(null);
  const [startAt, setStartAt] = useState(null);
  const [durationSec, setDurationSec] = useState(null);
  const [remaining, setRemaining] = useState(null);
  const [liveId, setLiveId] = useState(null);
  const [participantName, setParticipantName] = useState('')

  const pin = useMemo(() => pinDigits.join(''), [pinDigits]);

  useEffect(() => {
    const socket = io(SOCKET_URL, { transports: ['websocket'] });
    socketRef.current = socket;

    socket.on('participant:join_error', ({ message }) => {
      setError(message);
      setStatus('idle');
    });

    socket.on('pair:ready', ({ room, scenarioId }) => {
      setRoom(room);
      setScenarioId(scenarioId);
      setStatus('waiting');
    });

    socket.on('scenario:started', ({ livescenarioid, startAt, durationSec }) => {
      setLiveId(livescenarioid);
      setStartAt(startAt ?? Date.now());
      setDurationSec(durationSec ?? 0);
      setStatus('running');
    });

    socket.on('scenario:stopped', () => {
      setStatus('ended');
      setStartAt(null);
      setDurationSec(null);
      setRemaining(null);
      setLiveId(null);
    });

    //socket.on('peer:disconnected', () => {
    //  setStatus('idle');
    //  setRoom(null);
    //  setScenarioId(null);
      //setLiveId(null);
      //setError('Facilitator disconnected. Please rejoin with a new PIN.');
    //});

    return () => socket.disconnect();
  }, []);

  useEffect(() => {
    if (!startAt || !durationSec) return;
    const tick = () => {
      const end = startAt + durationSec * 1000;
      const msLeft = Math.max(0, end - Date.now());
      setRemaining(Math.ceil(msLeft / 1000));
    };
    tick();
    const id = setInterval(tick, 250);
    return () => clearInterval(id);
  }, [startAt, durationSec]);

  const join = () => {
    setError(null);

    if (pin.length !== 5) {
      setError('Please enter the full 5-digit PIN.');
      return;
    }

    setStatus('joining');
    socketRef.current?.emit('participant:join', { pin, name: participantName });
  };

  const resetHome = () => {
    setStatus('idle');
    setRoom(null);
    setScenarioId(null);
    setLiveId(null);
    setPinDigits(['', '', '', '', '']);
    setError(null);
  };

  if (status === 'running' && liveId) {
    return (
      <SessionContext.Provider value={{ socket: socketRef.current, room, liveId }}>
        <ParticipantEPRProvider liveId={liveId}>
          <EPRSocketBridge socket={socketRef.current} liveId={liveId} />
          <ParticipantApp />
        </ParticipantEPRProvider>
      </SessionContext.Provider>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: PAGE_BG }}>
      {/* Header */}
      <Box
        sx={{
          height: 72,
          px: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          bgcolor: HEADER_BG,
          color: 'white',
          borderBottom: `1px solid ${HEADER_BORDER}`,
        }}
      >
        <Stack direction="row" spacing={1.25} alignItems="center">
          <img src="/ssiconfull.png" alt="logo" style={{ width:'160px'}} />
        </Stack>

        <Button
          variant="outlined"
          startIcon={<CachedIcon />}
          onClick={() => navigate('/app/')}
          sx={{
            color: 'white',
            borderColor: 'rgba(255,255,255,0.35)',
            textTransform: 'none',
            bgcolor: 'rgba(255,255,255,0.06)',
            '&:hover': {
              borderColor: 'rgba(255,255,255,0.5)',
              bgcolor: 'rgba(255,255,255,0.12)',
            },
          }}
        >
          Facilitator mode
        </Button>
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2,
          py: 4,
        }}
      >
        {(status === 'idle' || status === 'joining') && (
          <StateCard
            icon={<GroupsOutlinedIcon sx={{ fontSize: 30 }} />}
            title="Join scenario"
            subtitle="Enter the 5-digit PIN provided by the facilitator to connect to the session."
          >
            <Stack spacing={2.5}>
              <PinInput
                value={pinDigits}
                onChange={setPinDigits}
                disabled={status === 'joining'}
              />
              <Box sx={{width:'70%', alignSelf:'center'}}>
              <TextField
                size="small"
                fullWidth
                value={participantName}
                placeholder="Enter your name"
                onChange={(e) => setParticipantName(e.target.value)}
                label="Name (optional)"
              />
              </Box>

              <Button
                onClick={join}
                variant="contained"
                disabled={status === 'joining'}
                endIcon={status === 'joining' ? null : <ArrowForwardIcon />}
                sx={{
                  textTransform: 'none',
                  height: 48,
                  fontWeight: 600,
                }}
              >
                {status === 'joining' ? 'Joining…' : 'Join scenario'}
              </Button>

              {error && (
                <Typography color="error" sx={{ textAlign: 'center' }}>
                  {error}
                </Typography>
              )}

            </Stack>
          </StateCard>
        )}

        {status === 'waiting' && (
          <StateCard
            icon={<CheckCircleOutlineIcon sx={{ fontSize: 30 }} />}
            title="Connected"
            subtitle="You are connected to the session and ready to begin."
          >
            <Stack spacing={2.5} alignItems="center">
              <Chip
                label={scenarioId ? `Scenario #${scenarioId}` : 'Connected'}
                color="success"
                variant="outlined"
              />

              <CircularProgress size={30} />

              <Typography sx={{ textAlign: 'center' }}>
                Waiting for the facilitator to start the scenario…
              </Typography>

              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                Keep this screen open. The session will launch automatically.
              </Typography>
            </Stack>
          </StateCard>
        )}

        {status === 'ended' && (
          <StateCard
            icon={<TaskAltIcon sx={{ fontSize: 30 }} />}
            title="Scenario complete"
            subtitle="This session has been ended by the facilitator."
          >
            <Stack spacing={3} alignItems="center">
              <Box
                sx={{
                  width: 84,
                  height: 84,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'rgba(34,197,94,0.10)',
                  border: '1px solid rgba(34,197,94,0.25)',
                }}
              >
                <TaskAltIcon sx={{ fontSize: 42, color: '#16a34a' }} />
              </Box>

              <Stack spacing={1} alignItems="center">
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Session ended
                </Typography>
                <Typography variant="body1" sx={{ textAlign: 'center' }}>
                  The facilitator has closed this scenario.
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', maxWidth: 360 }}>
                  You can return to the join screen and enter a new 5-digit PIN to connect to another session.
                </Typography>
              </Stack>

              <Button
                variant="contained"
                startIcon={<RestartAltIcon />}
                onClick={resetHome}
                sx={{
                  textTransform: 'none',
                  height: 46,
                  minWidth: 190,
                  fontWeight: 600,
                }}
              >
                Join another scenario
              </Button>
            </Stack>
          </StateCard>
        )}
      </Box>
    </Box>
  );
}

function StateCard({ icon, title, subtitle, children }) {
  return (
    <Paper
      elevation={0}
      sx={{
        width: '100%',
        maxWidth: 520,
        border: `1px solid ${CARD_BORDER}`,
        borderRadius: 3,
        overflow: 'hidden',
        bgcolor: 'white',
      }}
    >
      <Box
        sx={{
          px: 2.5,
          py: 1.5,
          bgcolor: CARD_HEADER_BG,
          borderBottom: `1px solid ${CARD_BORDER}`,
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', color: '#1e3a8a' }}>
          {icon}
        </Box>
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ p: 3 }}>{children}</Box>
    </Paper>
  );
}

function PinInput({ value, onChange, disabled = false }) {
  const refs = useRef([]);

  const updateDigit = (index, digit) => {
    const next = [...value];
    next[index] = digit;
    onChange(next);
  };

  const handleChange = (index, rawValue) => {
    const digits = rawValue.replace(/\D/g, '');

    if (!digits) {
      updateDigit(index, '');
      return;
    }

    if (digits.length === 1) {
      updateDigit(index, digits);
      if (index < 4) refs.current[index + 1]?.focus();
      return;
    }

    // handle paste or autofill into one box
    const next = [...value];
    let cursor = index;

    for (const d of digits.slice(0, 5 - index)) {
      next[cursor] = d;
      cursor += 1;
      if (cursor > 4) break;
    }

    onChange(next);

    const focusIndex = Math.min(index + digits.length, 4);
    refs.current[focusIndex]?.focus();
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace') {
      if (value[index]) {
        updateDigit(index, '');
        return;
      }

      if (index > 0) {
        refs.current[index - 1]?.focus();
        updateDigit(index - 1, '');
      }
    }

    if (e.key === 'ArrowLeft' && index > 0) {
      refs.current[index - 1]?.focus();
    }

    if (e.key === 'ArrowRight' && index < 4) {
      refs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 5);
    if (!pasted) return;

    const next = ['', '', '', '', ''];
    pasted.split('').forEach((d, i) => {
      next[i] = d;
    });

    onChange(next);
    refs.current[Math.min(pasted.length, 5) - 1]?.focus();
  };

  return (
    <Stack spacing={1.25} alignItems="center">
      <Typography variant="body2" color="text.secondary">
        Enter PIN
      </Typography>

      <Stack direction="row" spacing={1.25} justifyContent="center" onPaste={handlePaste}>
        {value.map((digit, index) => (
          <TextField
            key={index}
            inputRef={(el) => {
              refs.current[index] = el;
            }}
            value={digit}
            onChange={(e) => handleChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            disabled={disabled}
            inputProps={{
              maxLength: 1,
              inputMode: 'numeric',
              style: {
                textAlign: 'center',
                fontSize: '1.4rem',
                fontWeight: 700,
                padding: '10px 0',
              },
            }}
            sx={{
              width: 58,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                bgcolor: '#fff',
              },
            }}
          />
        ))}
      </Stack>
    </Stack>
  );
}